import { Assessment } from '../../../services/assessment/assessment.mapping'

/**
 * ポーリング中の `getAssessment` 全再取得・`this.assessment` 全再代入をまたいで
 * 保持する確定フィールド(transcript / summary)の値。
 *
 * transcript / summary は処理中に一度確定すると以後不変なので、`@skip` で
 * レスポンスから外し、ここに保持した値を `businessMeeting` へ埋め戻すことで
 * 全消費箇所(desktop / mobile / ダウンロード)の表示を維持する。
 */
export interface RetainedStableFields {
  transcript: Assessment['businessMeeting']['transcript']
  summary: Assessment['businessMeeting']['summary']
}

/** transcript が取得済み(1件以上の本文を持つ)か */
function hasTranscript(
  transcript: Assessment['businessMeeting']['transcript'],
): boolean {
  return !!transcript && transcript.length > 0
}

/** summary 本文が取得済みか */
function hasSummary(
  summary: Assessment['businessMeeting']['summary'],
): boolean {
  return summary != null
}

/** transcript を取得済み(1件以上)とみなし `@skip` 対象にしてよいか */
export function shouldSkipTranscript(retained: RetainedStableFields): boolean {
  return hasTranscript(retained.transcript)
}

/** summary 本文を取得済みとみなし `@skip` 対象にしてよいか */
export function shouldSkipSummary(retained: RetainedStableFields): boolean {
  return hasSummary(retained.summary)
}

/**
 * 全再取得した assessment に保持済みの transcript / summary を反映する。
 *
 * - レスポンスに本文があれば保持値を更新する(初回取得・以後の確定値)
 * - `@skip` でレスポンスから外れている(transcript=undefined / summary=null)場合は
 *   保持値を `businessMeeting` に埋め戻す
 *
 * `assessment.businessMeeting` を破壊的に更新し、次ポーリングへ持ち越す保持値を返す。
 */
export function applyRetainedStableFields(
  assessment: Assessment,
  retained: RetainedStableFields,
): RetainedStableFields {
  const businessMeeting = assessment.businessMeeting
  let { transcript, summary } = retained

  if (hasTranscript(businessMeeting.transcript)) {
    transcript = businessMeeting.transcript
  } else if (hasTranscript(transcript)) {
    businessMeeting.transcript = transcript
  }

  if (hasSummary(businessMeeting.summary)) {
    summary = businessMeeting.summary
  } else if (hasSummary(summary)) {
    businessMeeting.summary = summary
  }

  return { transcript, summary }
}
